import './choice-list.css';

/**
 * A list of described options to pick one from — for a DECISION that needs
 * explaining (vs `segmented.js`, a compact pill for short, obvious options).
 * Each option is a full-width button with a bold label, an optional one-line
 * description and an optional right-hand `detail` (e.g. the result of picking
 * it: "AU$38.20 per pay"), updatable later via `setDetail`.
 *
 * ARIA: a `radiogroup` of `radio` buttons (roving tabindex; arrow keys move and
 * pick). `onChange(value)` fires on EVERY pick, even of the selected option, so
 * a question flow can advance on a re-pick.
 *
 *   const list = createChoiceList({
 *     ariaLabel: 'How do you pay for it?',
 *     options: [{ value: 'a', label: 'A', description: '…' }, …],
 *     value: null,
 *     onChange: (v) => …,
 *   });
 *   form.append(list.el);
 *
 * @returns {{ el: HTMLElement, value: any, setValue(v): void,
 *   setDetail(value, text): void, setOptionHidden(value, hidden): void }}
 */
export function createChoiceList({ options, value = null, ariaLabel, onChange, className } = {}) {
  const el = document.createElement('div');
  el.className = ['choice-list', className].filter(Boolean).join(' ');
  el.setAttribute('role', 'radiogroup');
  if (ariaLabel) el.setAttribute('aria-label', ariaLabel);

  const buttons = new Map();
  const details = new Map();
  let current = null;

  for (const opt of options) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'choice-option';
    btn.setAttribute('role', 'radio');
    btn.dataset.value = String(opt.value);

    const text = document.createElement('span');
    text.className = 'choice-option__text';
    const label = document.createElement('span');
    label.className = 'choice-option__label';
    label.textContent = opt.label;
    text.append(label);
    if (opt.description) {
      const desc = document.createElement('span');
      desc.className = 'choice-option__description';
      desc.textContent = opt.description;
      text.append(desc);
    }
    const detail = document.createElement('span');
    detail.className = 'choice-option__detail';
    detail.textContent = opt.detail ?? '';
    detail.hidden = !opt.detail;
    btn.append(text, detail);

    btn.addEventListener('click', () => pick(opt.value));
    btn.addEventListener('keydown', (e) => onKey(e, opt.value));
    buttons.set(opt.value, btn);
    details.set(opt.value, detail);
    el.append(btn);
  }

  function visibleValues() {
    return options.map((o) => o.value).filter((v) => !buttons.get(v).hidden);
  }

  function render() {
    const values = visibleValues();
    // Roving tabindex: the selected option (else the first) is the tab stop.
    const stop = values.includes(current) ? current : values[0];
    for (const [v, btn] of buttons) {
      const on = v === current;
      btn.setAttribute('aria-checked', String(on));
      btn.classList.toggle('is-selected', on);
      btn.tabIndex = v === stop ? 0 : -1;
    }
  }

  function setValue(v) {
    current = buttons.has(v) ? v : null;
    render();
  }

  function pick(v) {
    setValue(v);
    onChange?.(v);
  }

  function onKey(e, v) {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const values = visibleValues();
    const next = values[(values.indexOf(v) + step + values.length) % values.length];
    pick(next);
    buttons.get(next).focus();
  }

  setValue(value);

  return {
    el,
    get value() {
      return current;
    },
    setValue,
    setDetail(v, text) {
      const d = details.get(v);
      if (!d) return;
      d.textContent = text ?? '';
      d.hidden = !text;
    },
    setOptionHidden(v, hidden) {
      const btn = buttons.get(v);
      if (!btn) return;
      btn.hidden = hidden;
      if (hidden && current === v) current = null;
      render();
    },
  };
}
